import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { securityAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface ScanModule {
  id: string;
  name: string;
  status: 'pending' | 'scanning' | 'completed' | 'failed';
  issues: number;
}

interface ScanStatus {
  scan_id: number;
  status: string;
  progress: number;
  scanned_pages: number;
  total_pages: number;
  issues_found: number;
  pages_crawled?: number;
  forms_found?: number;
  error_message?: string;
}

const SCAN_MODULES: { [key: string]: string } = {
  ssl: 'SSL/TLS Certificate',
  headers: 'Security Headers',
  cookies: 'Cookie Security',
  csrf: 'CSRF Protection',
  mixed_content: 'Mixed Content',
  information_disclosure: 'Information Disclosure',
  server_config: 'Server Configuration',
  cors: 'CORS Policy',
  clickjacking: 'Clickjacking Protection',
  ports: 'Port Security',
  server_fingerprint: 'Server Fingerprint',
  forms_deep: 'Form Security',
  best_practices: 'Best Practices'
};

const SecurityScanProgressPage: React.FC = () => {
  useDocumentTitle('Scan Progress');
  const navigate = useNavigate();
  const location = useLocation();
  const { currentAsset } = useAuth();

  // State from navigation
  const { scanId: initialScanId, modules: selectedModules = ['ssl', 'headers', 'clickjacking', 'exposure'], scanType = 'quick' } = (location.state as any) || {};

  const [scanId, setScanId] = useState<number | null>(initialScanId || null);
  const [status, setStatus] = useState<'initializing' | 'scanning' | 'completed' | 'failed'>('initializing');
  const [progress, setProgress] = useState(0);
  const [issuesFound, setIssuesFound] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentModule, setCurrentModule] = useState(0);
  const [modules, setModules] = useState<ScanModule[]>([]);
  const [pagesCrawled, setPagesCrawled] = useState(0);
  const [formsFound, setFormsFound] = useState(0);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastUpdateRef = useRef<number>(0);

  // Initialize modules list
  useEffect(() => {
    const moduleList: ScanModule[] = selectedModules.map((mod: string) => ({
      id: mod,
      name: SCAN_MODULES[mod] || mod.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
      status: 'pending',
      issues: 0
    }));
    setModules(moduleList);
  }, [selectedModules]);

  // Start scan if no scanId provided
  useEffect(() => {
    if (!scanId && currentAsset) {
      startScan();
    } else if (scanId) {
      setStatus('scanning');
      startPolling(scanId);
      startTimer();
    }

    return () => {
      cleanup();
    };
  }, [currentAsset]);

  const cleanup = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
  };

  const startScan = async () => {
    if (!currentAsset) return;

    try {
      const response = await securityAPI.runVulnerabilityScan(currentAsset.id, {
        modules: selectedModules
      });

      if (response.data.status === 'success') {
        const result = response.data.data;
        setScanId(result.scan_id);
        setStatus('scanning');
        startPolling(result.scan_id);
        startTimer();
      }
    } catch (error: any) {
      console.error('Failed to start scan:', error);
      setStatus('failed');
      toast.error(error.response?.data?.message || 'Failed to start scan');
    }
  };

  const startPolling = (id: number) => {
    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await securityAPI.getScanStatus(id);
        if (response.data.status === 'success') {
          const scanStatus: ScanStatus = response.data.data;

          // Calculate progress percentage
          const completedModules = scanStatus.scanned_pages || 0;
          const totalModules = modules.length || selectedModules.length;
          const calculatedProgress = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

          setProgress(calculatedProgress);
          setIssuesFound(scanStatus.issues_found || 0);
          setPagesCrawled(scanStatus.pages_crawled || 0);
          setFormsFound(scanStatus.forms_found || 0);

          // Update module statuses with smooth transition
          const now = Date.now();
          if (now - lastUpdateRef.current > 500) { // Debounce updates
            lastUpdateRef.current = now;
            setCurrentModule(completedModules);

            setModules(prev => prev.map((mod, idx) => ({
              ...mod,
              status: idx < completedModules ? 'completed' : idx === completedModules ? 'scanning' : 'pending'
            })));
          }

          if (scanStatus.status === 'completed') {
            handleScanComplete(scanStatus);
          } else if (scanStatus.status === 'failed') {
            handleScanFailed(scanStatus.error_message);
          }
        }
      } catch (error) {
        console.error('Error polling scan status:', error);
      }
    }, 1500); // Poll every 1.5 seconds
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
  };

  const handleScanComplete = (scanStatus: ScanStatus) => {
    cleanup();
    setStatus('completed');
    setProgress(100);

    // Mark all modules as completed
    setModules(prev => prev.map(mod => ({ ...mod, status: 'completed' })));

    // Show completion modal
    setShowCompletionModal(true);
  };

  const handleScanFailed = (errorMessage?: string) => {
    cleanup();
    setStatus('failed');
    toast.error(`Scan failed: ${errorMessage || 'Unknown error'}`);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const viewResults = () => {
    navigate('/security/results', {
      state: { scanId, fromScan: true }
    });
  };

  // Canvas animation for fiber optic network visualization
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Data packets flowing along fiber connections
    interface DataPacket {
      connectionIndex: number;
      progress: number;
      speed: number;
      color: string;
      size: number;
      direction: 'in' | 'out';
    }

    // Security node positions
    interface SecurityNode {
      x: number;
      y: number;
      angle: number;
      icon: string;
      label: string;
      moduleIndex: number;
    }

    const dataPackets: DataPacket[] = [];
    let securityNodes: SecurityNode[] = [];
    let centerX = 0;
    let centerY = 0;

    const resizeCanvas = () => {
      const container = canvas.parentElement;
      if (container) {
        canvas.width = container.clientWidth;
        canvas.height = container.clientHeight;
        centerX = canvas.width / 2;
        centerY = canvas.height / 2;

        // Recalculate node positions in a circle
        const nodeRadius = Math.min(canvas.width, canvas.height) * 0.32;
        const nodeIcons = ['🔒', '🛡️', '🔐', '⚡', '🌐', '🔑', '📡', '🛑', '🔍', '⚙️', '🛡️', '🔰', '✓'];

        securityNodes = modules.map((mod, idx) => {
          const angle = (idx / modules.length) * Math.PI * 2 - Math.PI / 2;
          return {
            x: centerX + Math.cos(angle) * nodeRadius,
            y: centerY + Math.sin(angle) * nodeRadius,
            angle,
            icon: nodeIcons[idx % nodeIcons.length],
            label: mod.name,
            moduleIndex: idx
          };
        });
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    let time = 0;

    // Spawn data packets
    const spawnPacket = () => {
      if (modules.length === 0 || status === 'completed') return;

      const activeModuleIndex = modules.findIndex(m => m.status === 'scanning');
      const targetIndex = activeModuleIndex >= 0 ? activeModuleIndex : Math.floor(Math.random() * modules.length);

      const colors = ['#3b82f6', '#22c55e', '#06b6d4', '#8b5cf6', '#f59e0b'];

      dataPackets.push({
        connectionIndex: targetIndex,
        progress: 0,
        speed: 0.012 + Math.random() * 0.008,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 3 + Math.random() * 2,
        direction: Math.random() > 0.3 ? 'out' : 'in'
      });
    };

    const packetInterval = setInterval(spawnPacket, 120);

    const animate = () => {
      // Clear canvas
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      time += 0.016;

      // Draw subtle grid background
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.05)';
      ctx.lineWidth = 1;
      const gridSize = 40;
      for (let x = 0; x < canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      for (let y = 0; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      // Draw central hub
      const hubRadius = 55 + Math.sin(time * 2) * 3;

      // Hub outer glow
      const hubGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, hubRadius * 2.5);
      hubGradient.addColorStop(0, status === 'completed' ? 'rgba(34, 197, 94, 0.25)' : 'rgba(59, 130, 246, 0.25)');
      hubGradient.addColorStop(0.5, status === 'completed' ? 'rgba(34, 197, 94, 0.08)' : 'rgba(59, 130, 246, 0.08)');
      hubGradient.addColorStop(1, 'rgba(59, 130, 246, 0)');
      ctx.beginPath();
      ctx.arc(centerX, centerY, hubRadius * 2.5, 0, Math.PI * 2);
      ctx.fillStyle = hubGradient;
      ctx.fill();

      // Hub outer ring
      ctx.beginPath();
      ctx.arc(centerX, centerY, hubRadius, 0, Math.PI * 2);
      ctx.strokeStyle = status === 'completed' ? 'rgba(34, 197, 94, 0.6)' : 'rgba(59, 130, 246, 0.6)';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Hub inner circle
      ctx.beginPath();
      ctx.arc(centerX, centerY, hubRadius - 12, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(15, 23, 42, 0.95)';
      ctx.fill();
      ctx.strokeStyle = status === 'completed' ? 'rgba(34, 197, 94, 0.4)' : 'rgba(99, 102, 241, 0.4)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw rotating scanner line (only when scanning)
      if (status !== 'completed') {
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(time * 1.5);
        const scanGrad = ctx.createLinearGradient(0, 0, hubRadius - 15, 0);
        scanGrad.addColorStop(0, 'rgba(34, 197, 94, 0.9)');
        scanGrad.addColorStop(1, 'rgba(34, 197, 94, 0)');
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(hubRadius - 15, 0);
        ctx.strokeStyle = scanGrad;
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.restore();
      }

      // Draw fiber connections and nodes
      securityNodes.forEach((node, idx) => {
        const mod = modules[idx];
        if (!mod) return;

        // Control point for curved connection
        const midX = (centerX + node.x) / 2;
        const midY = (centerY + node.y) / 2;
        const perpAngle = node.angle + Math.PI / 2;
        const curveOffset = 25 * Math.sin(time * 0.5 + idx);
        const ctrlX = midX + Math.cos(perpAngle) * curveOffset;
        const ctrlY = midY + Math.sin(perpAngle) * curveOffset;

        // Fiber cable glow for active/completed
        if (mod.status === 'scanning' || mod.status === 'completed') {
          ctx.beginPath();
          ctx.moveTo(centerX, centerY);
          ctx.quadraticCurveTo(ctrlX, ctrlY, node.x, node.y);
          ctx.strokeStyle = mod.status === 'completed' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(59, 130, 246, 0.25)';
          ctx.lineWidth = 10;
          ctx.stroke();
        }

        // Main fiber cable
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.quadraticCurveTo(ctrlX, ctrlY, node.x, node.y);
        ctx.strokeStyle = mod.status === 'completed' ? 'rgba(34, 197, 94, 0.8)' :
                          mod.status === 'scanning' ? 'rgba(59, 130, 246, 0.9)' :
                          'rgba(71, 85, 105, 0.4)';
        ctx.lineWidth = mod.status === 'scanning' ? 3 : 2;
        ctx.stroke();

        // Node size
        const nodeRadius = mod.status === 'scanning' ? 30 : 26;

        // Node glow for active
        if (mod.status === 'scanning') {
          const glowSize = 45 + Math.sin(time * 4) * 8;
          const nodeGlow = ctx.createRadialGradient(node.x, node.y, nodeRadius, node.x, node.y, glowSize);
          nodeGlow.addColorStop(0, 'rgba(59, 130, 246, 0.35)');
          nodeGlow.addColorStop(1, 'rgba(59, 130, 246, 0)');
          ctx.beginPath();
          ctx.arc(node.x, node.y, glowSize, 0, Math.PI * 2);
          ctx.fillStyle = nodeGlow;
          ctx.fill();
        }

        // Node background
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeRadius, 0, Math.PI * 2);
        ctx.fillStyle = mod.status === 'completed' ? 'rgba(34, 197, 94, 0.15)' :
                        mod.status === 'scanning' ? 'rgba(59, 130, 246, 0.15)' :
                        'rgba(30, 41, 59, 0.9)';
        ctx.fill();

        // Node border
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeRadius, 0, Math.PI * 2);
        ctx.strokeStyle = mod.status === 'completed' ? '#22c55e' :
                          mod.status === 'scanning' ? '#3b82f6' :
                          'rgba(100, 116, 139, 0.5)';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Rotating progress arc for scanning
        if (mod.status === 'scanning') {
          ctx.beginPath();
          const arcProgress = (time * 2) % (Math.PI * 2);
          ctx.arc(node.x, node.y, nodeRadius + 5, -Math.PI / 2, -Math.PI / 2 + arcProgress);
          ctx.strokeStyle = '#22c55e';
          ctx.lineWidth = 3;
          ctx.lineCap = 'round';
          ctx.stroke();
          ctx.lineCap = 'butt';
        }

        // Checkmark for completed
        if (mod.status === 'completed') {
          ctx.beginPath();
          ctx.moveTo(node.x - 10, node.y);
          ctx.lineTo(node.x - 3, node.y + 8);
          ctx.lineTo(node.x + 12, node.y - 8);
          ctx.strokeStyle = '#22c55e';
          ctx.lineWidth = 3;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();
          ctx.lineCap = 'butt';
          ctx.lineJoin = 'miter';
        }

        // Icon for pending/scanning
        if (mod.status !== 'completed') {
          ctx.font = `${nodeRadius * 0.7}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = mod.status === 'scanning' ? '#fff' : 'rgba(148, 163, 184, 0.7)';
          ctx.fillText(node.icon, node.x, node.y);
        }

        // Module label
        const labelY = node.y + nodeRadius + 18;
        ctx.font = '11px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = mod.status === 'completed' ? '#22c55e' :
                        mod.status === 'scanning' ? '#60a5fa' :
                        'rgba(148, 163, 184, 0.6)';
        ctx.fillText(mod.name.length > 15 ? mod.name.slice(0, 15) + '...' : mod.name, node.x, labelY);
      });

      // Draw and update data packets
      for (let i = dataPackets.length - 1; i >= 0; i--) {
        const packet = dataPackets[i];
        packet.progress += packet.speed;

        if (packet.progress >= 1) {
          dataPackets.splice(i, 1);
          continue;
        }

        const node = securityNodes[packet.connectionIndex];
        if (!node) continue;

        const t = packet.direction === 'out' ? packet.progress : 1 - packet.progress;
        const midX = (centerX + node.x) / 2;
        const midY = (centerY + node.y) / 2;
        const perpAngle = node.angle + Math.PI / 2;
        const curveOffset = 25 * Math.sin(time * 0.5 + packet.connectionIndex);
        const ctrlX = midX + Math.cos(perpAngle) * curveOffset;
        const ctrlY = midY + Math.sin(perpAngle) * curveOffset;

        // Bezier position
        const x = (1 - t) * (1 - t) * centerX + 2 * (1 - t) * t * ctrlX + t * t * node.x;
        const y = (1 - t) * (1 - t) * centerY + 2 * (1 - t) * t * ctrlY + t * t * node.y;

        // Packet glow
        ctx.beginPath();
        ctx.arc(x, y, packet.size * 2.5, 0, Math.PI * 2);
        const packetGlow = ctx.createRadialGradient(x, y, 0, x, y, packet.size * 2.5);
        packetGlow.addColorStop(0, packet.color.replace(')', ', 0.4)').replace('rgb', 'rgba').replace('#', 'rgba('));
        packetGlow.addColorStop(1, 'transparent');
        ctx.fillStyle = packetGlow;
        ctx.fill();

        // Packet core
        ctx.beginPath();
        ctx.arc(x, y, packet.size, 0, Math.PI * 2);
        ctx.fillStyle = packet.color;
        ctx.fill();

        // Trail effect
        for (let j = 1; j <= 4; j++) {
          const trailT = t - j * 0.015;
          if (trailT < 0) continue;
          const trailX = (1 - trailT) * (1 - trailT) * centerX + 2 * (1 - trailT) * trailT * ctrlX + trailT * trailT * node.x;
          const trailY = (1 - trailT) * (1 - trailT) * centerY + 2 * (1 - trailT) * trailT * ctrlY + trailT * trailT * node.y;

          ctx.beginPath();
          ctx.arc(trailX, trailY, packet.size * (1 - j * 0.2), 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${packet.color === '#3b82f6' ? '59,130,246' : packet.color === '#22c55e' ? '34,197,94' : '99,102,241'}, ${0.4 - j * 0.08})`;
          ctx.fill();
        }
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      clearInterval(packetInterval);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [status, modules]);

  return (
    <div className="security-scan-progress min-vh-100 d-flex flex-column" style={{
      background: '#0f172a'
    }}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
          display: inline-block;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        .pulse {
          animation: pulse 1.5s ease-in-out infinite;
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .fade-in-up {
          animation: fadeInUp 0.5s ease-out forwards;
        }
        .glow-text {
          text-shadow: 0 0 20px rgba(59, 130, 246, 0.5), 0 0 40px rgba(59, 130, 246, 0.3);
        }
        .progress-ring {
          transform: rotate(-90deg);
        }
        .progress-ring-circle {
          transition: stroke-dashoffset 0.5s ease;
        }
        .completion-modal {
          animation: fadeInUp 0.4s ease-out;
        }
        .completion-icon {
          animation: scaleIn 0.5s ease-out 0.2s both;
        }
        @keyframes scaleIn {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .success-glow {
          box-shadow: 0 0 60px rgba(34, 197, 94, 0.4), 0 0 120px rgba(34, 197, 94, 0.2);
        }
      `}</style>

      {/* Header */}
      <div className="p-4 border-bottom border-secondary" style={{ background: 'rgba(30, 41, 59, 0.5)' }}>
        <div className="container-fluid">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <i className="bi bi-shield-check fs-3 text-primary me-3"></i>
              <div>
                <h5 className="text-white mb-0">Security Scan in Progress</h5>
                <small className="text-secondary">
                  {currentAsset?.name || currentAsset?.website_urls?.[0] || 'Unknown'}
                </small>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3">
              <span className={`badge ${
                scanType === 'full' ? 'bg-danger' :
                scanType === 'standard' ? 'bg-warning text-dark' :
                'bg-info'
              } px-3 py-2`}>
                {scanType === 'full' ? 'Full Scan' :
                 scanType === 'standard' ? 'Standard Scan' :
                 'Quick Scan'}
              </span>
              <span className="badge bg-dark px-3 py-2">
                <i className="bi bi-clock me-2"></i>
                {formatTime(elapsedTime)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Centered Animation */}
      <div className="flex-grow-1 position-relative">
        <canvas
          ref={canvasRef}
          className="position-absolute w-100 h-100"
          style={{ top: 0, left: 0 }}
        />

        {/* Center Progress Overlay */}
        <div className="position-absolute top-50 start-50 translate-middle text-center" style={{ zIndex: 10 }}>
          {/* Circular Progress Ring */}
          <div className="position-relative d-inline-block">
            <svg width="180" height="180" className="progress-ring">
              <circle
                cx="90"
                cy="90"
                r="80"
                fill="none"
                stroke="rgba(59, 130, 246, 0.15)"
                strokeWidth="6"
              />
              <circle
                className="progress-ring-circle"
                cx="90"
                cy="90"
                r="80"
                fill="none"
                stroke={status === 'completed' ? '#22c55e' : '#3b82f6'}
                strokeWidth="6"
                strokeDasharray={502.65}
                strokeDashoffset={502.65 - (502.65 * progress) / 100}
                strokeLinecap="round"
              />
            </svg>
            <div className="position-absolute top-50 start-50 translate-middle text-center">
              <div className={`display-5 fw-bold text-white ${status === 'scanning' ? 'pulse' : ''}`} style={{ textShadow: '0 0 30px rgba(59, 130, 246, 0.5)' }}>
                {Math.round(progress)}%
              </div>
              <div className={`small ${status === 'completed' ? 'text-success' : 'text-primary'}`}>
                {status === 'completed' ? 'Complete' : 'Scanning'}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Stats Bar */}
        <div className="position-absolute bottom-0 start-0 end-0 p-4" style={{ background: 'linear-gradient(transparent, rgba(15, 23, 42, 0.9))' }}>
          <div className="container">
            <div className="row text-center">
              <div className="col">
                <div className="text-white fs-4 fw-bold">{currentModule}/{modules.length}</div>
                <div className="text-secondary small">Modules</div>
              </div>
              <div className="col">
                <div className="text-warning fs-4 fw-bold">{issuesFound}</div>
                <div className="text-secondary small">Issues Found</div>
              </div>
              <div className="col">
                <div className="text-info fs-4 fw-bold">{pagesCrawled}</div>
                <div className="text-secondary small">Pages Crawled</div>
              </div>
              <div className="col">
                <div className="text-purple fs-4 fw-bold" style={{ color: '#a78bfa' }}>{formsFound}</div>
                <div className="text-secondary small">Forms Found</div>
              </div>
            </div>

            {/* Current Action */}
            {status === 'scanning' && modules[currentModule] && (
              <div className="text-center mt-3">
                <span className="badge bg-primary bg-opacity-25 text-primary px-3 py-2">
                  <i className="bi bi-arrow-repeat spin me-2"></i>
                  Analyzing: {modules[currentModule]?.name}
                </span>
              </div>
            )}

            {/* Cancel Button */}
            {status === 'scanning' && (
              <div className="text-center mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => navigate('/security/scanning')}
                >
                  <i className="bi bi-x-lg me-2"></i>
                  Cancel
                </button>
                <p className="text-secondary small mt-2 mb-0">Scan continues in background if you leave</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Completion Modal */}
      {showCompletionModal && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1000, background: 'rgba(15, 23, 42, 0.9)', backdropFilter: 'blur(8px)' }}>
          <div className="completion-modal text-center p-5 rounded-4 success-glow" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', maxWidth: '450px', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
            {/* Success Icon */}
            <div className="completion-icon mb-4">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: '100px', height: '100px', background: 'rgba(34, 197, 94, 0.15)', border: '3px solid #22c55e' }}>
                <i className="bi bi-check-lg text-success" style={{ fontSize: '3.5rem' }}></i>
              </div>
            </div>

            <h3 className="text-white mb-2">Scan Complete!</h3>
            <p className="text-secondary mb-4">
              Security analysis finished successfully
            </p>

            {/* Stats */}
            <div className="row g-3 mb-4">
              <div className="col-4">
                <div className="p-3 rounded" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
                  <div className="text-white fs-4 fw-bold">{modules.length}</div>
                  <div className="text-secondary small">Modules</div>
                </div>
              </div>
              <div className="col-4">
                <div className="p-3 rounded" style={{ background: issuesFound > 0 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(34, 197, 94, 0.1)' }}>
                  <div className={`fs-4 fw-bold ${issuesFound > 0 ? 'text-warning' : 'text-success'}`}>{issuesFound}</div>
                  <div className="text-secondary small">Issues</div>
                </div>
              </div>
              <div className="col-4">
                <div className="p-3 rounded" style={{ background: 'rgba(99, 102, 241, 0.1)' }}>
                  <div className="text-white fs-4 fw-bold">{formatTime(elapsedTime)}</div>
                  <div className="text-secondary small">Duration</div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <button
              className="btn btn-success btn-lg w-100 mb-3"
              onClick={viewResults}
            >
              <i className="bi bi-file-earmark-bar-graph me-2"></i>
              View Results
            </button>
            <button
              className="btn btn-outline-secondary w-100"
              onClick={() => navigate('/security/scanning')}
            >
              Start New Scan
            </button>
          </div>
        </div>
      )}

      {/* Failed State */}
      {status === 'failed' && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1000, background: 'rgba(15, 23, 42, 0.9)', backdropFilter: 'blur(8px)' }}>
          <div className="text-center p-5 rounded-4" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', maxWidth: '400px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
            <div className="mb-4">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: '80px', height: '80px', background: 'rgba(239, 68, 68, 0.15)', border: '3px solid #ef4444' }}>
                <i className="bi bi-x-lg text-danger" style={{ fontSize: '2.5rem' }}></i>
              </div>
            </div>

            <h4 className="text-white mb-2">Scan Failed</h4>
            <p className="text-secondary mb-4">
              An error occurred during the security scan. Please try again.
            </p>

            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary flex-grow-1"
                onClick={() => navigate('/security/scanning')}
              >
                Go Back
              </button>
              <button
                className="btn btn-primary flex-grow-1"
                onClick={startScan}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                Retry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityScanProgressPage;
